import React, { useEffect, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, CheckIcon } from 'lucide-react';
interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  courseTitle: string;
  price?: number;
  isSpecialized?: boolean;
}
export function PaymentModal({
  isOpen,
  onClose,
  courseTitle,
  price,
  isSpecialized = false
}: PaymentModalProps) {
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://js.paystack.co/v1/inline.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  const handlePayment = (amount: number, type: string) => {
    if (typeof (window as any).PaystackPop === 'undefined') {
      alert('Payment system is loading. Please try again in a moment.');
      return;
    }
    const handler = (window as any).PaystackPop.setup({
      key: 'pk_live_da1302c981176c1062619bdfe51974de4c0595e7',
      email: 'customer@email.com',
      amount: amount * 100,
      currency: 'NGN',
      ref: `LMA-${Date.now()}`,
      metadata: {
        custom_fields: [{
          display_name: 'Course',
          variable_name: 'course',
          value: courseTitle
        }, {
          display_name: 'Class Type',
          variable_name: 'class_type',
          value: type
        }]
      },
      callback: function (response: any) {
        alert(`Payment successful! Reference: ${response.reference}`);
        onClose();
      },
      onClose: function () {
        alert('Payment window closed');
      }
    });
    handler.openIframe();
  };
  return <AnimatePresence data-id="element-275">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={onClose} data-id="element-276" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-277">
            <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" data-id="element-278">
              <div className="sticky top-0 bg-white border-b border-slate-200 p-6 flex items-center justify-between" data-id="element-279">
                <h2 className="text-2xl font-bold text-slate-900" data-id="element-280">
                  {isSpecialized ? 'Enroll Now' : 'Choose Your Plan'}
                </h2>
                <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-slate-100 transition-colors duration-200" data-id="element-281">
                  <XIcon size={24} data-id="element-282" />
                </button>
              </div>
              <div className="p-6" data-id="element-283">
                <div className="mb-6" data-id="element-284">
                  <h3 className="text-xl font-semibold text-slate-900 mb-2" data-id="element-285">
                    {courseTitle}
                  </h3>
                  <p className="text-slate-600" data-id="element-286">
                    {isSpecialized ? 'Complete your enrollment for this specialized program' : 'Select the class type that works best for you'}
                  </p>
                </div>
                {isSpecialized && price ? <div className="bg-gradient-to-br from-green-50 to-orange-50 rounded-2xl p-8 border-2 border-green-200" data-id="element-287">
                    <div className="text-center mb-6" data-id="element-288">
                      <div className="text-4xl font-bold text-green-600 mb-2" data-id="element-289">
                        ₦{price.toLocaleString()}
                      </div>
                    </div>
                    <button onClick={() => handlePayment(price, 'Specialized')} className="w-full bg-green-600 text-white py-4 rounded-full font-semibold hover:bg-green-700 transition-colors duration-200" data-id="element-290">
                      Proceed to Payment
                    </button>
                  </div> : <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-291">
                    {/* Group Class */}
                    <div className="bg-gradient-to-br from-green-50 to-orange-50 rounded-2xl p-8 border-2 border-green-200 hover:border-green-400 transition-all duration-300" data-id="element-292">
                      <div className="text-center mb-6" data-id="element-293">
                        <h4 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-294">
                          Group Class
                        </h4>
                        <div className="text-4xl font-bold text-green-600 mb-2" data-id="element-295">
                          ₦60,000
                        </div>
                        <p className="text-slate-600" data-id="element-296">4 weeks duration</p>
                      </div>
                      <ul className="space-y-3 mb-8" data-id="element-297">
                        <li className="flex items-center gap-2 text-slate-700" data-id="element-298">
                          <CheckIcon className="text-green-600" size={20} data-id="element-299" />
                          Interactive group sessions
                        </li>
                        <li className="flex items-center gap-2 text-slate-700" data-id="element-300">
                          <CheckIcon className="text-green-600" size={20} data-id="element-301" />
                          Learn with peers
                        </li>
                        <li className="flex items-center gap-2 text-slate-700" data-id="element-302">
                          <CheckIcon className="text-green-600" size={20} data-id="element-303" />
                          Collaborative learning
                        </li>
                        <li className="flex items-center gap-2 text-slate-700" data-id="element-304">
                          <CheckIcon className="text-green-600" size={20} data-id="element-305" />
                          Course materials included
                        </li>
                      </ul>
                      <button onClick={() => handlePayment(60000, 'Group')} className="w-full bg-green-600 text-white py-4 rounded-full font-semibold hover:bg-green-700 transition-colors duration-200" data-id="element-306">
                        Select Group Class
                      </button>
                    </div>
                    {/* Private Class */}
                    <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-8 border-2 border-orange-400 text-white relative overflow-hidden" data-id="element-307">
                      <div className="absolute top-4 right-4 bg-orange-500 text-white px-4 py-1 rounded-full text-sm font-bold" data-id="element-308">
                        POPULAR
                      </div>
                      <div className="text-center mb-6" data-id="element-309">
                        <h4 className="text-2xl font-bold mb-2" data-id="element-310">
                          Private Class
                        </h4>
                        <div className="text-4xl font-bold text-orange-400 mb-2" data-id="element-311">
                          ₦170,000
                        </div>
                        <p className="text-slate-300" data-id="element-312">1 month duration</p>
                        <p className="text-sm text-orange-300 mt-2" data-id="element-313">
                          24 hours per month
                        </p>
                      </div>
                      <ul className="space-y-3 mb-8" data-id="element-314">
                        <li className="flex items-center gap-2" data-id="element-315">
                          <CheckIcon className="text-orange-400" size={20} data-id="element-316" />
                          One-on-one sessions
                        </li>
                        <li className="flex items-center gap-2" data-id="element-317">
                          <CheckIcon className="text-orange-400" size={20} data-id="element-318" />
                          Personalized curriculum
                        </li>
                        <li className="flex items-center gap-2" data-id="element-319">
                          <CheckIcon className="text-orange-400" size={20} data-id="element-320" />
                          Flexible scheduling
                        </li>
                        <li className="flex items-center gap-2" data-id="element-321">
                          <CheckIcon className="text-orange-400" size={20} data-id="element-322" />
                          Priority support
                        </li>
                        <li className="flex items-center gap-2" data-id="element-323">
                          <CheckIcon className="text-orange-400" size={20} data-id="element-324" />
                          Faster progress
                        </li>
                        <li className="flex items-center gap-2" data-id="element-325">
                          <CheckIcon className="text-orange-400" size={20} data-id="element-326" />
                          Recorded classes available
                        </li>
                      </ul>
                      <button onClick={() => handlePayment(170000, 'Private')} className="w-full bg-orange-500 text-white py-4 rounded-full font-semibold hover:bg-orange-600 transition-colors duration-200" data-id="element-327">
                        Select Private Class
                      </button>
                    </div>
                  </div>}
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}